import React from 'react';
import { Skeleton } from 'antd';
import { EmptyRendererComponent } from '@owl-frontend/components';
import { Locale } from '../../../../context/AppProvider';
import ClaimFileViewer from '../../../ClaimOverview/ClaimFilesContainer/ClaimFileViewer';
import { isPageStale } from '../../../ClaimOverview/ClaimFilesContainer/ClaimFileViewerContainer';
import { ClaimDetailsContext } from '../../ClaimDetailsContext';
import { fetchFileViewerType, FIRST_PAGE, PDF_RANGE } from '../../data/logic';
import useFetchFilesHook from './useFetchFilesHook';

interface Props {
  dossierId: string;
  fileId: string;
  defaultPage?: number;
}

const inFlightContentKeys = new Set<string>();

/**
 * ClaimFileViewerContainer
 * ========================
 * Displays files' source, and manages the Redux and render layer.
 *
 * On render, the container will do the following:
 * - fetch the file entity by file_id
 * - fetch all the related files' S3 file paths (its pages)
 *
 * Additional Notes
 * ----------------
 * - pages are 1-indexed
 */
const ClaimFileViewerContainer: React.FC<Props> = ({
  dossierId,
  fileId,
  defaultPage = FIRST_PAGE,
}) => {
  const { messages } = React.useContext(Locale);
  const { files } = React.useContext(ClaimDetailsContext);
  const [{ filePaths, actions }, handler] = useFetchFilesHook(dossierId);
  const [currPage, setCurrPage] = React.useState(defaultPage);

  // Invalidate currPage when opening new file
  // eslint-disable-next-line react-etc/prefer-usememo
  React.useEffect(() => {
    setCurrPage(defaultPage);
  }, [defaultPage, fileId]);

  React.useEffect(() => {
    if (files?.[fileId]) {
      return;
    }
    handler.fetchFile({ fileId });
  }, [fileId, files, handler]);

  // Tracks the (fileId, page) we last dispatched for, so cleanup can release
  // the right key regardless of subsequent page navigation. Without this, a
  // user paging forward before the prior fetch resolves would leak the prior
  // key into `inFlightContentKeys` permanently.
  const inFlightRef = React.useRef<{ fileId: string; page: number } | null>(
    null
  );

  React.useEffect(() => {
    if (!files || !files[fileId]) {
      return;
    }

    if (
      filePaths[fileId]?.[currPage] &&
      !isPageStale(filePaths[fileId][currPage].requestedDate)
    ) {
      return;
    }

    const inFlightKey = `${fileId}-${currPage}`;
    if (inFlightContentKeys.has(inFlightKey)) {
      return;
    }
    inFlightContentKeys.add(inFlightKey);
    inFlightRef.current = { fileId, page: currPage };

    const fileType = fetchFileViewerType(files[fileId]);
    if (fileType === 'PDF') {
      handler.fetchPdfDocFilePaths({
        dossierId: files[fileId].dossier_id,
        fileId,
        pages: [
          Math.max(FIRST_PAGE, currPage - PDF_RANGE),
          Math.min(currPage + PDF_RANGE, files[fileId].total_pages),
        ],
      });
    } else {
      handler.fetchFilePaths({
        fileType,
        dossierId: files[fileId].dossier_id,
        fileId,
      });
    }
  }, [currPage, fileId, filePaths, files, handler]);

  React.useEffect(() => {
    // Release the in-flight key this instance owns when its page lands OR the
    // paths action rejects. We key cleanup off `inFlightRef`, not the live
    // `currPage`, so paging forward before the prior fetch resolves doesn't
    // leak the old key.
    const inFlight = inFlightRef.current;
    if (!inFlight) {
      return;
    }
    const succeeded = !!filePaths[inFlight.fileId]?.[inFlight.page];
    const rejected = actions.fetchClaimFilePaths.status === 'rejected';
    if (!succeeded && !rejected) {
      return;
    }
    inFlightContentKeys.delete(`${inFlight.fileId}-${inFlight.page}`);
    inFlightRef.current = null;
  }, [filePaths, actions.fetchClaimFilePaths.status]);

  if (
    actions.fetchClaimFile.status === 'pending' ||
    actions.fetchClaimFilePaths.status === 'pending'
  ) {
    return (
      <div style={{ padding: '1rem' }}>
        <Skeleton paragraph={{ rows: 5 }} />
      </div>
    );
  }

  if (
    actions.fetchClaimFile.status === 'rejected' ||
    actions.fetchClaimFilePaths.status === 'rejected'
  ) {
    return (
      <EmptyRendererComponent
        landingMessage={messages['claim_overview.shell.file.failedFetch']}
      />
    );
  }

  // MARK: render PDF files
  return (
    <ClaimFileViewer
      fileUrls={Object.fromEntries(
        Object.entries(filePaths[fileId] ?? {}).map(([key, value]) => [
          parseInt(key, 10),
          value.url,
        ])
      )}
      currPage={currPage}
      totalPages={files?.[fileId]?.total_pages ?? 1}
      setCurrPage={function (page: number): void {
        setCurrPage(page);
      }}
    />
  );
};

export default ClaimFileViewerContainer;
